import { Injectable } from '@angular/core';
import { SelectableData } from 'app/modules/main/components/data-selector/data-selector.models';
import { SelectableDataColumn } from 'app/modules/main/components/data-selector/data-selector.models';
import { SelectableDataColumnExtractor } from 'app/modules/main/components/data-selector/data-selector.models';
import { PromotionalType } from 'app/modules/main/models/publisher-tags-and-promotional-types';
import { of } from 'rxjs';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PromotionalTypeColumnExtractor implements SelectableDataColumnExtractor<PromotionalType> {

  // select control column width is 13%
  //  -- all columns should add up to 100%
  readonly columns: SelectableDataColumn[] = [
    {
      headingLabel: $localize`:@@communication-centre.advertiser.promotional-type-selector.column.type.label:Type`,
      headingStyle: { width: '87%' },
      valueStyle: { width: '87%' },
    },
  ];

  private readonly adNetworksCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.ad-networks:Ad Networks`;
  private readonly mediaBrokersCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.media-brokers:Media Brokers`;
  private readonly subNetworksCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.sub-networks:Sub Networks`;
  private readonly directTrafficCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.direct-traffic:Direct Traffic`;
  private readonly socialTrafficCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.social-traffic:Social Traffic`;
  private readonly mobileTrafficCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.mobile-traffic:Mobile Traffic`;
  private readonly retargetingDisplayCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.retargeting-display:Retargeting (Display)`;
  private readonly contextualTargetingCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.contextual-targeting:Contextual Targeting`;

  private readonly directLinkingCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.direct-linking:Direct Linking`;
  private readonly linkingViaLandingPagesCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.linking-via-landing-pages:Linking via Landing Pages`;
  private readonly socialSearchCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.social-search:Social Search`;
  private readonly mobileSearchCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.mobile-search:Mobile Search`;
  private readonly domainParkingCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.domain-parking:Domain Parking`;

  private readonly comparisonEngineCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.comparison-engine:Comparison Engine`;
  private readonly shoppingDirectoryCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.shopping-directory:Shopping Directory`;
  private readonly editorialContentCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.editorial-content:Editorial Content`;
  private readonly socialContentCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.social-content:Content Creators & Influencers`;
  private readonly mediaContentCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.media-content:Media Content`;
  private readonly communitiesUgcCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.communities-ugc:Communities & UGC`;
  private readonly cashbackCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.cashback:Cashback`;
  private readonly loyaltyCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.loyalty:Loyalty`;
  private readonly discountCodeCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.discount-code:Discount Code`;
  private readonly leadGenerationContentCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.lead-generation-content:Lead Generation (Content)`;
  private readonly virtualIncentivesCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.virtual-incentives:Virtual Incentives`;
  private readonly comparisonShoppingServiceLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.comparison-shopping-service:Comparison Shopping Service (CSS)`;

  private readonly newslettersCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.newsletters:Newsletters`;
  private readonly leadGenerationEmailCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.lead-generation-email:Lead Generation (Email)`;
  private readonly retargetingEmailCategoryLabel = $localize`:@@communication-centre.advertiser.promotional-type-selector.category.retargeting-email:Retargeting (Email)`;

  getValue(
    index: number,
    selectablePromotionalType: SelectableData<PromotionalType>
  ): Observable<string> {

    const promotionalType = selectablePromotionalType.data;

    switch (index) {

      case 0:
        return of(this.localizeName(promotionalType.name));

      default:
        return of('');
    }
  }

  private localizeName(
    label: string
  ): string {

    switch (label) {

      case 'Ad Networks':
        return this.adNetworksCategoryLabel;

      case 'Media Brokers':
        return this.mediaBrokersCategoryLabel;

      case 'Sub Networks':
        return this.subNetworksCategoryLabel;

      case 'Direct Traffic':
        return this.directTrafficCategoryLabel;

      case 'Social Traffic':
        return this.socialTrafficCategoryLabel;

      case 'Mobile Traffic':
        return this.mobileTrafficCategoryLabel;

      case 'Retargeting (Display)':
        return this.retargetingDisplayCategoryLabel;

      case 'Contextual Targeting':
        return this.contextualTargetingCategoryLabel;

      case 'Direct Linking':
        return this.directLinkingCategoryLabel;

      case 'Linking via Landing Pages':
        return this.linkingViaLandingPagesCategoryLabel;

      case 'Social Search':
        return this.socialSearchCategoryLabel;

      case 'Mobile Search':
        return this.mobileSearchCategoryLabel;

      case 'Domain Parking':
        return this.domainParkingCategoryLabel;

      case 'Comparison Engine':
        return this.comparisonEngineCategoryLabel;

      case 'Shopping Directory':
        return this.shoppingDirectoryCategoryLabel;

      case 'Editorial Content':
        return this.editorialContentCategoryLabel;

      case 'Social Content':
        return this.socialContentCategoryLabel;

      case 'Media Content':
        return this.mediaContentCategoryLabel;

      case 'Communities & UGC':
        return this.communitiesUgcCategoryLabel;

      case 'Cashback':
        return this.cashbackCategoryLabel;

      case 'Loyalty':
        return this.loyaltyCategoryLabel;

      case 'Discount Code':
        return this.discountCodeCategoryLabel;

      case 'Lead Generation (Content)':
        return this.leadGenerationContentCategoryLabel;

      case 'Virtual Incentives':
        return this.virtualIncentivesCategoryLabel;

      case 'Newsletters':
        return this.newslettersCategoryLabel;

      case 'Lead Generation (Email)':
        return this.leadGenerationEmailCategoryLabel;

      case 'Retargeting (Email)':
        return this.retargetingEmailCategoryLabel;

      case 'Comparison Shopping Service (CSS)':
        return this.comparisonShoppingServiceLabel;

      default:
        return label;
    }
  }

}
